import { useState, useEffect, useRef } from "react";
import "@/App.css";
import emailjs from "@emailjs/browser";
import { 
  BarChart3, 
  Network, 
  TrendingUp, 
  Zap,
  MapPin,
  Phone,
  Mail,
  ChevronRight,
  Menu,
  X,
  CheckCircle,
  Target,
  Shield,
  Lightbulb,
  Send,
  ArrowRight
} from "lucide-react";

// Assets
const LOGO = "https://customer-assets.emergentagent.com/job_cedis-pro/artifacts/w0k15sxb_Cedis%20del%20Sur%20SVG.svg";
const HERO_BG = "https://images.pexels.com/photos/221047/pexels-photo-221047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Methodology Images
const METHODOLOGY_IMAGES = [
  "https://customer-assets.emergentagent.com/job_cedis-pro/artifacts/p8n87sdb_Imagen1.jpg",
  "https://customer-assets.emergentagent.com/job_cedis-pro/artifacts/peoexbo3_Imagen2.jpg",
  "https://customer-assets.emergentagent.com/job_cedis-pro/artifacts/616qf8hb_Imagen3.jpg",
  "https://customer-assets.emergentagent.com/job_cedis-pro/artifacts/ijk9r6aa_Imagen4.jpg"
];

// EmailJS Configuration
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// Navigation Component
const Header = ({ scrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  const navLinks = [
    { href: "#inicio", label: "Inicio" },
    { href: "#servicios", label: "Servicios" },
    { href: "#pilares", label: "Pilares" },
    { href: "#metodologia", label: "Metodología" },
    { href: "#contacto", label: "Contacto" }
  ];

  return (
    <header 
      data-testid="main-header"
      className="bg-white sticky top-0 z-50 shadow-sm"
    >
      <div className="cedis-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#inicio" data-testid="logo-link" className="flex-shrink-0">
            <img 
              src={LOGO} 
              alt="CEDIS del Sur" 
              style={{ height: '7rem' }}
              className="w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className="font-semibold transition-colors text-[#0B3041] hover:text-[#C04F15]" style={{ fontSize: '1.2rem' }}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-[#0B3041] relative z-50"
          >
            {mobileMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-[#0B3041]/95 backdrop-blur-xl z-40 flex items-center justify-center lg:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                onClick={() => setMobileMenuOpen(false)}
                className="text-2xl font-bold text-white hover:text-[#FFC000] transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="inicio" 
      data-testid="hero-section"
      className="hero-section relative flex items-center"
      style={{ backgroundImage: `url(${HERO_BG})` }}
    >
      <div className="hero-overlay"></div>
      
      <div className="cedis-container relative z-10 py-20 lg:py-32">
        <div className="max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase text-[#FFC000] mb-6 font-semibold">
            Motor Estratégico de la Cadena de Suministro
          </p>
          
          <h1 
            data-testid="hero-title"
            className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-8 leading-[1.05]"
          >
            Transformamos la <span className="text-[#FFC000]">logística</span> en ventaja competitiva
          </h1>
          
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-xl">
            Especializados en intermediación comercial al por mayor y menor. 
            Conectamos fabricantes con el consumidor final a través de una 
            infraestructura robusta y eficiente.
          </p>
        </div>
      </div>

      {/* Marquee */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0B3041]/80 py-4">
        <div className="marquee-container">
          <div className="marquee-content">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center gap-12 px-6">
                {["DISTRIBUCIÓN", "MAYORISTA", "MINORISTA", "LOGÍSTICA", "CONFIANZA", "EFICIENCIA", "ALCANCE"].map((word, idx) => (
                  <span key={idx} className="text-white/70 text-sm font-semibold tracking-widest flex items-center gap-12">
                    {word}
                    <span className="w-2 h-2 bg-[#FFC000] rounded-full"></span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: BarChart3,
      title: "Inteligencia Logística de Mercado",
      description: "No solo almacenamos; utilizamos el análisis de datos para entender el comportamiento minorista, reduciendo el stock muerto y elevando la rotación con precisión quirúrgica."
    },
    {
      icon: Network,
      title: "Conectividad Omnicanal Estratégica",
      description: "Funcionamos como el puente definitivo entre la planta y el punto de venta, integrando a los proveedores con las principales cadenas de retail para eliminar fricciones."
    },
    {
      icon: TrendingUp,
      title: "Optimización de la Rentabilidad",
      description: "Transformamos el gasto logístico en una inversión directa, maximizando el margen de beneficio de nuestros socios mediante la mejora continua y la reducción de tiempos."
    },
    {
      icon: Zap,
      title: "Agilidad y Capacidad de Respuesta",
      description: "Nuestra velocidad operativa asegura que los picos de demanda se cubran sin contratiempos, manteniendo la cadena de suministro activa incluso en los escenarios más exigentes."
    }
  ];

  return (
    <section id="servicios" data-testid="services-section" className="cedis-section bg-[#F4F6F8]">
      <div className="cedis-container">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-[#C04F15] mb-4 font-semibold">
            Propuesta de Valor
          </p>
          <h2 data-testid="services-title" className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0B3041] mb-6">
            Servicios que impulsan tu negocio
          </h2>
          <p className="text-[#4B5563] text-lg">
            En CEDIS del Sur, operamos como el motor estratégico de la cadena de suministro, especializándonos en la intermediación comercial al por mayor y menor. Transformamos la logística tradicional en una ventaja competitiva, conectando a fabricantes con el consumidor final a través de una infraestructura robusta. Nuestro enfoque se centra en convertir el inventario en flujo de efectivo inmediato, asegurando que cada producto domine su segmento de mercado mediante una distribución capilar y eficiente.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <div 
              key={index}
              data-testid={`service-card-${index}`}
              className="service-card"
            >
              <div className="w-14 h-14 flex items-center justify-center mb-6 bg-[#C04F15]/10">
                <service.icon className="w-7 h-7 text-[#C04F15]" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-[#0B3041]">
                {service.title}
              </h3>
              <p className="text-[#4B5563]">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Cedis Services Section
const CedisServicesSection = () => {
  const cedisServices = [
    {
      title: "Intermediación de Comercio al por Menor y Mayor",
      description: "Actuamos como el nexo vital entre fabricantes y diversos estratos comerciales, gestionando la colocación estratégica de productos para maximizar su presencia en el mercado final."
    },
    {
      title: "Venta al por Mayor por Comisión",
      description: "Ejecutamos la comercialización de grandes volúmenes de inventario bajo un esquema de resultados, utilizando nuestra fuerza de ventas para cerrar contratos efectivos y asegurar la rotación constante."
    },
    {
      title: "Venta por Consignación Estratégica",
      description: "Administramos el stock del productor en nuestros centros de control, asumiendo la responsabilidad de su desplazamiento y cuidado hasta el momento de la venta efectiva al consumidor."
    },
    {
      title: "Gestión de Logística y Distribución Capilar",
      description: "Implementamos sistemas de transporte especializados que permiten que la mercancía llegue a los puntos de venta más atomizados, garantizando una cobertura territorial que otros no alcanzan."
    },
    {
      title: "Sincronización de la Cadena de Suministro",
      description: "Optimizamos los tiempos de tránsito y reducimos costos operativos mediante la planificación de rutas y la consolidación de carga, transformando la logística en una inversión rentable para el socio."
    },
    {
      title: "Asesoría en Inteligencia de Mercado y PDV",
      description: "Proporcionamos datos críticos sobre el comportamiento del consumidor en el punto de venta (PDV), permitiendo ajustar la oferta y asegurar que el producto siempre cumpla su promesa de marca."
    }
  ];

  return (
    <section id="servicios-cedis" data-testid="cedis-services-section" className="cedis-section bg-white">
      <div className="cedis-container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-[#C04F15] mb-4 font-semibold">
            Lo Que Hacemos
          </p>
          <h2 data-testid="cedis-services-title" className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0B3041] mb-6">
            Servicios Cedis del Sur
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cedisServices.map((service, index) => (
            <div 
              key={index}
              data-testid={`cedis-service-${index}`}
              className="group relative bg-[#F4F6F8] p-8 border-l-4 border-[#C04F15] hover:bg-[#0B3041] transition-all duration-300"
            >
              <span className="absolute top-4 right-4 text-5xl font-black text-[#0B3041]/10 group-hover:text-white/10 transition-colors">
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3 className="text-xl font-bold text-[#0B3041] group-hover:text-white mb-4 transition-colors pr-12">
                {service.title}
              </h3>
              <p className="text-[#4B5563] group-hover:text-white/80 transition-colors">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Methodology Section
const MethodologySection = () => {
  const phases = [
    {
      number: "01",
      title: "Fase de Intermediación y Alcance",
      description: "Iniciamos con la gestión total del canal minorista y la colocación estratégica en zonas de alta demanda, permitiendo que el fabricante se concentre exclusivamente en la producción.",
      image: METHODOLOGY_IMAGES[0]
    },
    {
      number: "02",
      title: "Protocolo de Eficiencia Operativa",
      description: "Implementamos la reducción de costos de distribución y optimizamos los tiempos de entrega, convirtiendo la logística en un sistema que paga su propia inversión a través del ahorro.",
      image: METHODOLOGY_IMAGES[1]
    },
    {
      number: "03",
      title: "Marco de Seguridad Jurídica y Comercial",
      description: "Formalizamos el objeto del contrato con cláusulas de cumplimiento (SLA) y niveles de servicio que blindan la integridad de la cadena y definen responsabilidades claras para ambas partes.",
      image: METHODOLOGY_IMAGES[2]
    },
    {
      number: "04",
      title: "Consolidación y Transparencia Financiera",
      description: "Establecemos esquemas de remuneración auditables y vigencias basadas en metas, asegurando una relación de confianza mutua a largo plazo donde el crecimiento del socio es nuestro éxito.",
      image: METHODOLOGY_IMAGES[3]
    }
  ];

  return (
    <section id="metodologia" data-testid="methodology-section" className="cedis-section bg-white">
      <div className="cedis-container">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <p className="text-xs tracking-[0.3em] uppercase text-[#C04F15] mb-4 font-semibold">
            Nuestra Metodología
          </p>
          <h2 data-testid="methodology-title" className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0B3041] mb-6">
            Sincronización Progresiva
          </h2>
          <p className="text-[#4B5563] text-lg">
            Un enfoque sistemático que garantiza resultados medibles en cada etapa del proceso.
          </p>
        </div>

        <div className="space-y-20 lg:space-y-32">
          {phases.map((phase, index) => (
            <div 
              key={index}
              data-testid={`methodology-phase-${index + 1}`}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={`methodology-step ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <span className="methodology-number">{phase.number}</span>
                <div className="methodology-content">
                  <h3 className="text-2xl sm:text-3xl font-bold text-[#0B3041] mb-4">
                    {phase.title}
                  </h3>
                  <p className="text-[#4B5563] text-lg leading-relaxed">
                    {phase.description}
                  </p>
                </div>
              </div>
              
              <div className={`img-zoom ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <img 
                  src={phase.image} 
                  alt={phase.title}
                  className="w-full h-[300px] md:h-[400px] object-cover"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pillars Section
const PillarsSection = () => {
  const pillars = [
    {
      icon: Target,
      title: "Optimización del Canal Minorista",
      description: "Actuamos como un filtro de eficiencia, asumiendo la responsabilidad total del posicionamiento y la promesa de marca en el punto de venta final."
    },
    {
      icon: MapPin,
      title: "Garantía de Alcance Capilar",
      description: "Implementamos sistemas de distribución que penetran en sectores de difícil acceso, funcionando como el brazo comercial experto que asegura la disponibilidad inmediata."
    },
    {
      icon: Lightbulb,
      title: "Inteligencia de Mercado",
      description: "Recopilamos y analizamos datos críticos del consumidor en tiempo real para asesorar a nuestros proveedores y garantizar contratos basados en resultados medibles."
    },
    {
      icon: Shield,
      title: "Integridad Operativa",
      description: "Nos conducimos bajo una ética de transparencia absoluta en la gestión de stock y procesos de intermediación, fortaleciendo la confianza a largo plazo."
    }
  ];

  return (
    <section id="pilares" data-testid="pillars-section" className="cedis-section bg-[#0B3041]">
      <div className="cedis-container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-[#FFC000] mb-4 font-semibold">
            Fundamentos
          </p>
          <h2 data-testid="pillars-title" className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6">
            Pilares de la Organización
          </h2>
          <p className="text-white/70 text-lg">
            Los valores y principios que guían cada una de nuestras operaciones.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((pillar, index) => (
            <div 
              key={index}
              data-testid={`pillar-card-${index}`}
              className="pillar-card bg-[#163E64] p-8"
            >
              <div className="w-12 h-12 bg-[#C04F15] flex items-center justify-center mb-6">
                <pillar.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                {pillar.title}
              </h3>
              <p className="text-white/70">
                {pillar.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es requerido';
    if (!formData.email.trim()) {
      newErrors.email = 'El correo es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ingresa un correo válido';
    }
    if (!formData.message.trim()) newErrors.message = 'El mensaje es requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setSubmitStatus(null);

    try {
      const templateParams = {
        from_name: formData.name,
        from_company: formData.company || 'No especificada',
        from_email: formData.email,
        message: formData.message,
        to_email: 'contacto@cedisdelsur.com'
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      setSubmitStatus({ type: 'success', message: '¡Mensaje enviado correctamente! Nos pondremos en contacto pronto.' });
      setFormData({ name: '', company: '', email: '', message: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus({ type: 'error', message: 'Error al enviar el mensaje. Por favor, intenta de nuevo.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="cedis-section bg-[#163E64]">
      <div className="cedis-container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Side - Info */}
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#FFC000] mb-4 font-semibold">
              Contacto
            </p>
            <h2 data-testid="contact-title" className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-8">
              Hablemos de negocios
            </h2>
            <p className="text-white/70 text-lg mb-12">
              Estamos listos para convertir tu logística en una ventaja competitiva. 
              Contáctanos y descubre cómo podemos impulsar tu negocio.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#C04F15] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Dirección</h4>
                  <p className="text-white/70">
                    Calle Esteban Alatorre No. 1288A Int. 1,<br />
                    CP 44360, Guadalajara, Jalisco, México
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#C04F15] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Teléfono</h4>
                  <p className="text-white/70">311 375 0844</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#C04F15] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Correo</h4>
                  <p className="text-white/70">
                    contacto@cedisdelsur.com<br />
                    ventas@cedisdelsur.com
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div>
            {submitStatus && (
              <div 
                data-testid="form-status"
                className={`mb-6 p-4 ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-500/20 border border-green-500 text-green-400' 
                    : 'bg-red-500/20 border border-red-500 text-red-400'
                }`}
              >
                {submitStatus.message}
              </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Nombre completo *"
                    data-testid="input-name"
                    className={`contact-input ${errors.name ? 'border-red-500' : ''}`}
                    disabled={isLoading}
                  />
                  {errors.name && <p className="error-message">{errors.name}</p>}
                </div>

                <div>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Empresa (opcional)"
                    data-testid="input-company"
                    className="contact-input"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Correo electrónico *"
                    data-testid="input-email"
                    className={`contact-input ${errors.email ? 'border-red-500' : ''}`}
                    disabled={isLoading}
                  />
                  {errors.email && <p className="error-message">{errors.email}</p>}
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tu mensaje *"
                    rows="4"
                    data-testid="input-message"
                    className={`contact-textarea ${errors.message ? 'border-red-500' : ''}`}
                    disabled={isLoading}
                  />
                  {errors.message && <p className="error-message">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  data-testid="submit-btn"
                  disabled={isLoading}
                  className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span>
                      Enviando...
                    </>
                  ) : (
                    <>
                      Enviar Mensaje
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#0B3041] py-16 md:py-20">
      <div className="cedis-container">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex flex-col items-center md:items-start gap-4">
            <img 
              src={LOGO} 
              alt="CEDIS del Sur" 
              className="w-auto"
              style={{ height: '6rem', filter: 'brightness(0) invert(1)' }}
            />
            <p className="text-white/50 text-sm">
              El motor estratégico de tu cadena de suministro.
            </p>
          </div>

          <div className="text-center md:text-right">
            <p className="text-white/70 mb-2">
              Esteban Alatorre No. 1288A Int. 1, CP 44360
            </p>
            <p className="text-white/50 text-sm">
              © {currentYear} Distribuidora CEDIS del Sur. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="App" data-testid="app-container">
      <Header 
        scrolled={scrolled} 
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <main>
        <HeroSection />
        <ServicesSection />
        <CedisServicesSection />
        <PillarsSection />
        <MethodologySection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
